import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation } from "react-router";

import { SortColumns } from "../components/Umpires/types/umpires";
import { UmpiresContextProps, UmpiresContextValues } from "../types/contexts";

import { SortOrders } from "../../../../types/shared";
import { IUmpireUser } from "../../../../types/api";

const UmpiresContext = createContext<UmpiresContextValues | null>(null);

export const useUmpiresContext = () => {
    const context = useContext(UmpiresContext);
    if (!context) {
        throw new Error(
            "useUmpiresContext must be used within a UmpiresProvider",
        );
    }
    return context;
};

export const UmpiresProvider: React.FC<UmpiresContextProps> = props => {
    const { children, initialUmpires } = props;
    const [fullUmpiresList, setFullUmpiresList] =
        useState<IUmpireUser[]>(initialUmpires);
    const [sortColumn, setSortColumn] = useState<SortColumns>("email");
    const [sortOrder, setSortOrder] = useState<SortOrders>("ascending");
    const [searchValue, setSearchValue] = useState<string>("");
    const [type, setType] = useState<IUmpireUser["status"]>("active");
    const location = useLocation();

    useEffect(() => {
        setSearchValue("");
    }, [location]);

    useEffect(() => {
        setFullUmpiresList(initialUmpires);
    }, [initialUmpires]);

    const filteredUmpires = useMemo(() => {
        let filteredUmpires = fullUmpiresList.filter(u => u.status === type);

        if (searchValue) {
            const search = searchValue.toLowerCase();
            filteredUmpires = filteredUmpires.filter(u => {
                return (
                    u.meta.firstName.toLowerCase().includes(search) ||
                    u.meta.lastName.toLowerCase().includes(search) ||
                    u.email.toLowerCase().includes(search) ||
                    u.meta.city.toLowerCase().includes(search) ||
                    u.meta.state.toLowerCase().includes(search) ||
                    u.meta?.phoneNumbers?.primary
                        ?.toLowerCase()
                        .includes(search)
                );
            });
        }

        return filteredUmpires;
    }, [fullUmpiresList, type, searchValue]);

    const sortedUmpires = useMemo(() => {
        let aValue: any;
        let bValue: any;
        return [...filteredUmpires].sort((a, b) => {
            switch (sortColumn) {
                case "email":
                    aValue = a.email.toLowerCase();
                    bValue = b.email.toLowerCase();
                    break;
                case "firstName":
                    aValue = a.meta.firstName.toLowerCase();
                    bValue = b.meta.firstName.toLowerCase();
                    break;
                case "lastName":
                    aValue = a.meta.lastName.toLowerCase();
                    bValue = b.meta.lastName.toLowerCase();
                    break;
                case "city":
                    aValue = a.meta.city.toLowerCase();
                    bValue = b.meta.city.toLowerCase();
                    break;
                case "state":
                    aValue = a.meta.state.toLowerCase();
                    bValue = b.meta.state.toLowerCase();
                    break;
                case "games":
                    aValue = a.games;
                    bValue = b.games;
                    break;
                case "ratings":
                    aValue = a.ratings;
                    bValue = b.ratings;
                    break;
            }

            if (sortOrder === "ascending") {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    }, [filteredUmpires, sortColumn, sortOrder]);

    const contextValue = useMemo(
        () => ({
            umpires: sortedUmpires,
            setUmpires: setFullUmpiresList,
            totalItems: sortedUmpires.length,
            sortColumn,
            setSortColumn,
            sortOrder,
            setSortOrder,
            searchValue,
            setSearchValue,
            type,
            setType,
        }),
        [sortedUmpires, type, sortColumn, sortOrder, searchValue],
    );

    return (
        <UmpiresContext.Provider value={contextValue}>
            {children}
        </UmpiresContext.Provider>
    );
};
